import axios, { AxiosError } from 'axios';

import {
  GlobalProjectSearchParams,
  ProjectNumberType,
  SearchConfig,
} from '../interface/app-header';
import { URL_CONSTANTS } from '../../../../constants/urlConstants';

let controller: AbortController | null = null;

const SEARCH_CONFIG: Record<string, SearchConfig> = {
  project_number: {
    url: 'projects/v1/search-by-project-number',
    query: 'project_number',
    extra: '&is_suggestion_enabled=',
  },
  po_number: {
    url: 'projects/v1/search-by-po-number',
    query: 'po_number',
    extra: '&is_suggestion_enabled=',
  },
  client: {
    url: 'customers/report/search-by-name',
    query: 'searchString',
    extra: '&offset=0&limit=20',
  },
  phone: {
    url: 'customers/report/search-by-phone-number',
    query: 'searchString',
  },
  address: {
    url: 'projects/search-by-address',
    query: 'k',
  },
};

export const searchProjects = async ({
  searchValue,
  searchParameter,
  history,
  searchSuggession,
  setIsHardRefresh,
  setGlobalSearchLoader,
}: GlobalProjectSearchParams) => {
  if (!searchValue) return;

  controller?.abort();
  controller = new AbortController();

  const config = SEARCH_CONFIG[searchParameter];
  if (!config) return;

  const baseURL = URL_CONSTANTS.API.BASE_URL;
  const extraParam = config.extra
    ? config.extra.includes('=')
      ? `${config.extra}${searchSuggession}`
      : config.extra
    : '';

  const finalURL = `${baseURL}/${config.url}?${config.query}=${searchValue}${extraParam}`;

  try {
    setGlobalSearchLoader?.(true);

    const response = await axios.get(finalURL, {
      signal: controller.signal,
    });

    const responseData = response?.data;
    if (!responseData) {
      setGlobalSearchLoader?.(false);
      return [];
    }

    // Handle auto-redirect for project/PO search
    if (
      !searchSuggession &&
      ['project_number', 'po_number'].includes(searchParameter) &&
      responseData.length
    ) {
      let projectId: string | null = null;

      if (responseData.length === 1) {
        projectId = responseData[0].project_id;
      } else {
        projectId = responseData.find(
          (record: ProjectNumberType) =>
            parseInt(searchValue) === parseInt(record.project_number as string)
        )?.project_id;
      }

      if (projectId) {
        const { pathname } = window.location;
        const parts = pathname.split('/');
        const redirectPath =
          parts[1] === 'crm'
            ? `/crm/${parts[2]}/${parts[3]}/project/view/${parts.at(-1)}`
            : `/project/view/${projectId}`;

        history.push(
          `${redirectPath}/#tab=scheduleInfo?searchType=${searchParameter}&searchValue=${searchValue}`
        );
      }
    }

    // Extract data depending on API structure
    const finalData = ['project_number', 'po_number', 'address'].includes(
      searchParameter
    )
      ? responseData
      : responseData.items;

    setIsHardRefresh?.(p => !p);
    setGlobalSearchLoader?.(false);

    return finalData;
  } catch (error) {
    console.error(error);
    setGlobalSearchLoader?.(false);
  }
};

export const updateTheme = async (theme: string) => {
  try {
    const response = await axios.patch(
      `${URL_CONSTANTS.API.BASE_URL}/auth/user/update-theme-selection`,
      {
        selected_theme: theme,
      }
    );
    return {
      error: false,
      themeData: response,
    };
  } catch (error: unknown) {
    console.error('Error fetching issues data:', error);
    return {
      error: true,
      message:
        error instanceof AxiosError
          ? error?.response?.data?.error
          : 'Error fetching issues data',
      themeData: null,
    };
  }
};
